import { useToast } from 'context/tostifyContext';
import { useEffect, useState } from 'react';
import customAxios from 'routes/Pages/util/Api';
import { updateRecordModifiedDate } from 'routes/Pages/util/CommonAPIs';
import authHeader from 'services/auth/jwt/auth-header';

const boolToString = value => (value ? 'true' : 'false');
const stringToBool = value => (value === 'true' || value === 'Yes' ? true : false);

export const useQuestionnaire = ({ data }) => {
  const { showToast } = useToast();

  const [questionnaire, setQuestionnaire] = useState({
    id: null,
    firstThinnings: false,
    secondThinnings: false,
    clearFell: false,
    treesLeftPerHectareIfDiff: '',
    noteIfYouNeedDifferentQtys: '',
    doYouRequireHardCopy: '',
    archaeologicalArea: false,
    recreationalArea: false,
    waterways: false,
    powerOrPhoneLines: false,
    railLines: false,
    nearbyDwellings: false,
    otherPleaseDetail: false,
    isPermitDocRequired: '',
    isSupplierDocRequired: '',
  });

  const [accountDetails, setAccountDetails] = useState({
    id: null,
    accountHolderName: '',
    bankName: '',
    iban: '',
    bic: '',
  });

  const [vatRate, setVatRates] = useState('');
  const [otherVatRate, setOtherVatRate] = useState(false);

  useEffect(() => {
    getQuestionnaireData();
    getAccountHolderData();
    getVatRate();
  }, [data.contractUniqueID]);

  console.log('data ===', data);

  const normalizeQuestionnairePayload = q => ({
    ...q,
    firstThinnings: boolToString(q.firstThinnings),
    secondThinnings: boolToString(q.secondThinnings),
    clearFell: boolToString(q.clearFell),
    archaeologicalArea: boolToString(q.archaeologicalArea),
    recreationalArea: boolToString(q.recreationalArea),
    waterways: boolToString(q.waterways),
    powerOrPhoneLines: boolToString(q.powerOrPhoneLines),
    railLines: boolToString(q.railLines),
    nearbyDwellings: boolToString(q.nearbyDwellings),
    otherPleaseDetail: boolToString(q.otherPleaseDetail),
  });

  const denormalizeQuestionnairePayload = q => ({
    ...q,
    firstThinnings: stringToBool(q.firstThinnings),
    secondThinnings: stringToBool(q.secondThinnings),
    clearFell: stringToBool(q.clearFell),
    archaeologicalArea: stringToBool(q.archaeologicalArea),
    recreationalArea: stringToBool(q.recreationalArea),
    waterways: stringToBool(q.waterways),
    powerOrPhoneLines: stringToBool(q.powerOrPhoneLines),
    railLines: stringToBool(q.railLines),
    nearbyDwellings: stringToBool(q.nearbyDwellings),
    otherPleaseDetail: stringToBool(q.otherPleaseDetail),
  });

  const getQuestionnaireData = async () => {
    try {
      const response = await customAxios.get(`/Ops/GetContractsQuestionnaire?contractUniqueID=${data.contractUniqueID}`, {
        headers: authHeader(),
      });

      if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
        const denormalized = denormalizeQuestionnairePayload(response.data[0]);
        setQuestionnaire(denormalized);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getAccountHolderData = async () => {
    try {
      const response = await customAxios.get(`/Ops/GetContractsAccountingInfo?contractUniqueID=${data.contractUniqueID}`, {
        headers: authHeader(),
      });

      if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
        setAccountDetails(response.data[0]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getVatRate = async () => {
    try {
      const response = await customAxios.get(`/CRM/GetCRMSiteDetails?siteAssessmentId=${data.siteAssessmentID}`, {
        headers: authHeader(),
      });

      if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
        setVatRates(response.data[0]?.vatRate);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleQuestionnaireChange = (field, value) => {
    setQuestionnaire(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAccountChange = (field, value) => {
    setAccountDetails(prev => ({ ...prev, [field]: value }));
  };

  const onUpdate = async () => {
    try {
      const questionnairePayload = {
        ...normalizeQuestionnairePayload(questionnaire),
        contractUniqueID: data.contractUniqueID,
      };

      const questionnaireRequest = questionnaire.id
        ? customAxios.post('/Ops/EditContractsQuestionnaire', questionnairePayload, { headers: authHeader() })
        : (() => {
            const { id, ...payload } = questionnairePayload;
            return customAxios.post('/Ops/AddContractsQuestionnaire', payload, { headers: authHeader() });
          })();

      const accountDetailsData = {
        ...accountDetails,
        contractUniqueID: data.contractUniqueID,
      };

      const accountRequest = accountDetails.id
        ? customAxios.post('/Ops/EditContractsAccountingInfo', accountDetailsData, { headers: authHeader() })
        : (() => {
            const { id, ...payload } = accountDetailsData;
            return customAxios.post('/Ops/AddContractsAccountingInfo', payload, { headers: authHeader() });
          })();

      let updateCRMSitePromise = null;

      const { data: crmSiteDetails } = await customAxios.get(
        `/CRM/GetCRMSiteDetails?siteAssessmentId=${data.siteAssessmentID}`,
        {
          headers: authHeader(),
        },
      );

      if (crmSiteDetails?.length) {
        const existing = crmSiteDetails[0];
        const formData = new FormData();

        Object.entries(existing).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            formData.append(key, String(value));
          }
        });

        formData.set('vatRate', vatRate);

        updateCRMSitePromise = customAxios.post('/CRM/EditCRMSiteDetails', formData, { headers: authHeader() });
      }

      await Promise.all([questionnaireRequest, accountRequest, ...(updateCRMSitePromise ? [updateCRMSitePromise] : [])]);

      await updateRecordModifiedDate(data.siteAssessmentID);
      showToast('Questionnaire updated successfully', 'success');
    } catch (error) {
      console.error(error);
      showToast('Failed to update contract details', 'error');
    }
  };

  return {
    questionnaire,
    accountDetails,
    handleQuestionnaireChange,
    handleAccountChange,
    onUpdate,
    vatRate,
    setVatRates,
    otherVatRate,
    setOtherVatRate,
  };
};
